import { useFeatureFlagEnabled, usePostHog } from "@posthog/react";
import { useEffect, useState } from "react";
import { secondMs } from "verdi-common/timeConstants";
import { posthogEnabled } from "@/services/analytics/enabled";

/**
 * Feature flags served by PostHog. Use the PostHog UI to configure them and turn them on and off.
 *
 * The static config in feature-flags.ts still owns the older flags; this is the target pattern they migrate onto.
 *
 */
export const POSTHOG_FEATURE_FLAG = {
    ON_DEMAND_IRRIGATION: "on-demand-irrigation-launch",
    // Business requirement: Grandfathered orgs we owe on-demand irrigation regardless of how slowly their zones actuate
    ON_DEMAND_IRRIGATION_RESPONSE_TIME_BYPASS: "on-demand-irrigation-response-time-bypass",
    // Pre-release; target internal users only
    DATA_VIEWER: "data-viewer",
    // The one carve-out: orgs that get the data viewer on ePlant device cards before release
    EPLANT_DEVICE_CARD_DATA_VIEWER: "data-viewer-eplant-device-card",
    // Hides the station's marker, which is the only way to its card
    WEATHER_STATION: "weather-station",
    // Gates the rewritten add-device card; the legacy flow remains the fallback
    ADD_DEVICE: "add-device",
    // The zone card rebuilt on the panel stack and Coss; the legacy card is what everyone else sees
    NEW_ZONE_CARD: "new-zone-card",
    // The threshold-based events page in the settings menu; the route itself stays reachable by URL
    THRESHOLD_EVENT_EXPLORER: "threshold-event-explorer",
    // Shows the beta features settings card, where a user opts in and out of early access features; target internal users
    BETA_USER: "beta-user",
} as const;

export type PostHogFeatureFlag = (typeof POSTHOG_FEATURE_FLAG)[keyof typeof POSTHOG_FEATURE_FLAG];

/**
 * Whether a PostHog flag is on for the current organization. Flags load asynchronously, so this may initially be false for the first render or two
 *
 * Anything short of a definite yes reads as "off", so an unreachable PostHog or a flag that does not exist yet hides the feature.
 *
 * Local Dev exception: the posthog SDK is never booted there, so every flag is treated as ON rather than
 * hiding an in-development feature. Safe to call the hook regardless since it falls back to false if PostHog is not initialized.
 */
export function usePostHogFeatureFlag(flag: PostHogFeatureFlag): boolean {
    // false is the default value if the flag is not set or posthog unreachable
    const isEnabledInPostHog = useFeatureFlagEnabled(flag, false);

    return !posthogEnabled || isEnabledInPostHog;
}

// Over its flag quota, PostHog never reports flags as loaded, so a pending flag reads as off after this long
export const FLAG_PENDING_TIMEOUT_MS = 10 * secondMs;

/**
 * Like usePostHogFeatureFlag, but undefined until PostHog has answered this visit, for a view that would otherwise
 * show the wrong state while flags load. It ignores the value saved from the last visit, which may be out of date.
 * Once flags have loaded, failed to, or the wait times out, a flag PostHog did not return reads as off.
 */
export function usePostHogFeatureFlagOrPending(flag: PostHogFeatureFlag): boolean | undefined {
    const posthog = usePostHog();
    // fresh: undefined until this visit's flags arrive, rather than the value saved from the last visit
    const [isEnabledInPostHog, setIsEnabledInPostHog] = useState(() => posthog.isFeatureEnabled(flag, { fresh: true }));
    const [hasStoppedWaiting, setHasStoppedWaiting] = useState(() => posthog.featureFlags?.hasLoadedFlags ?? false);

    // The SDK calls back once flags load or fail to load, and straight away when they already have
    useEffect(
        () =>
            posthog.onFeatureFlags(() => {
                setIsEnabledInPostHog(posthog.isFeatureEnabled(flag, { fresh: true }));
                setHasStoppedWaiting(true);
            }),
        [posthog, flag],
    );

    useEffect(() => {
        const timeout = setTimeout(() => setHasStoppedWaiting(true), FLAG_PENDING_TIMEOUT_MS);
        return () => clearTimeout(timeout);
    }, []);

    if (!posthogEnabled) {
        return true;
    }
    return isEnabledInPostHog ?? (hasStoppedWaiting ? false : undefined);
}
